import { SchedulesManager } from "@/pages/human-resources/time-management/business/SchedulesManager.ts";
import { EmployeesContractsManager } from "@/pages/human-resources/time-management/business/EmployeesContractsManager.ts";
import { fetchSchedules } from "@/pages/human-resources/time-management/services/FetchSchedules.ts";
import { readEmployeesContracts } from "@/pages/human-resources/time-management/endpoints/contracts/EmployeesContractsEndpoint.ts";
import { mapToEmployee } from "@/pages/human-resources/time-management/endpoints/contracts/inout/EmployeeContractsOut.ts";
import type { ScheduleReferenceData } from "@/pages/human-resources/time-management/business/reference/ScheduleReferenceData.ts";

interface ScheduleManagerLoader {
  createScheduleManager: (
    refData: ScheduleReferenceData,
    startDate: Date,
    endDate: Date,
  ) => Promise<SchedulesManager>;
  updateScheduleManager: (
    refData: ScheduleReferenceData,
    schedulesManager: SchedulesManager,
  ) => Promise<SchedulesManager>;
}

export function createSchedulesManagerLoader(
  visibleSitesIds: Set<string>,
): ScheduleManagerLoader {
  // Create manager by loading contracts, then schedules:
  const createScheduleManager = async (
    refData: ScheduleReferenceData,
    startDate: Date,
    endDate: Date,
  ): Promise<SchedulesManager> => {
    const contractsManager = await loadContractsManager(
      refData.siteData.siteId,
      visibleSitesIds,
      startDate,
      endDate,
    );
    const schedules = await fetchSchedules(refData, contractsManager);
    return new SchedulesManager(contractsManager, schedules);
  };

  // Creates an updated manager with same contracts but fresh schedules:
  const updateScheduleManager = async (
    refData: ScheduleReferenceData,
    schedulesManager: SchedulesManager,
  ): Promise<SchedulesManager> => {
    const contractsMan = schedulesManager.contractsManager;
    const schedules = await fetchSchedules(refData, contractsMan);
    return new SchedulesManager(contractsMan, schedules);
  };

  return {
    createScheduleManager,
    updateScheduleManager,
  };
}

async function loadContractsManager(
  siteId: string,
  visibleSitesIds: Set<string>,
  startDate: Date,
  endDate: Date,
): Promise<EmployeesContractsManager> {
  return new EmployeesContractsManager(
    siteId,
    startDate,
    endDate,
    (await readEmployeesContracts(siteId, startDate, endDate)).map((e) =>
      mapToEmployee(siteId, e),
    ),
    visibleSitesIds,
  );
}
